import { useEffect, useState } from 'react';

import {
  PLAYBACK_RATE_DEFAULT,
  PLAYBACK_RATE_MAX,
  PLAYBACK_RATE_MIN,
  PLAYBACK_RATE_PRESETS,
} from '../constants';
import { isValidPlaybackRate } from '../logic';

/**
 * コンテンツ単位の再生速度倍率入力欄。
 *
 * - 現在値 (`value`) が未指定 or 1.0 の場合は「等倍再生」の 1.0 プリセットが選択状態になる。
 * - プリセット (0.75/0.85/1.0/1.15/1.25) と自由入力 (0.5〜2.0) の 2 方式で入力できる。
 * - 値変更時は親コンポーネントに数値または `undefined` を通知する。
 *   親側で `1.0 === PLAYBACK_RATE_DEFAULT` の場合は `playback_rate` プロパティを
 *   削除する (未指定と同義 = DynamoDB item 削減 + 後方互換維持)。
 *
 * a11y: プリセットボタンは group ロールで囲み、`aria-pressed` で選択状態を表現する。
 * 自由入力は `<input type="number">` を使用し、`min` / `max` / `step` で
 * ブラウザ標準のバリデーションを効かせる。
 */
export function PlaybackRateField({
  contentIndex,
  value,
  onChange,
}: {
  /** 親コンテンツの index (data-testid 用) */
  contentIndex: number;
  /** 現在の playback_rate (undefined = 未指定 = 等倍再生) */
  value: number | undefined;
  /** 値が変わったら呼ばれる (undefined = 未指定 / 1.0 = 等倍) */
  onChange: (nextRate: number | undefined) => void;
}): React.JSX.Element {
  // 現在値。未指定なら 1.0 (等倍) を表示する。
  const effective =
    typeof value === 'number' && isValidPlaybackRate(value) ? value : PLAYBACK_RATE_DEFAULT;

  // ユーザが自由入力欄に手入力した際の一時値 (数値以外 / 範囲外を許容し、
  // blur / Enter 時に確定する)。数値化できたらそのまま value にする方針でも
  // 良いが、桁数入力途中で親 state が壊れないようローカル state に保持する。
  const [rawInput, setRawInput] = useState<string>(String(effective));

  // 親から流れてくる value が変わった (別コンテンツを開いた等) 場合に
  // rawInput を追従させる。effective も再計算されるので依存に含める。
  useEffect(() => {
    setRawInput(String(effective));
  }, [effective]);

  const handlePresetClick = (rate: number): void => {
    if (!isValidPlaybackRate(rate)) return;
    setRawInput(String(rate));
    // 1.0 は「未指定と同義」として親側でキーを削除する。
    onChange(rate === PLAYBACK_RATE_DEFAULT ? undefined : rate);
  };

  const commitRawInput = (raw: string): void => {
    const parsed = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(parsed)) {
      // 数値化できない → 現在の effective に戻す
      setRawInput(String(effective));
      return;
    }
    // 範囲外はクランプして親に通知する (HTML5 バリデーション + JS 側の
    // 二重防御。ブラウザによっては min/max を超える値でも入力できるため)。
    const clamped = Math.min(PLAYBACK_RATE_MAX, Math.max(PLAYBACK_RATE_MIN, parsed));
    setRawInput(String(clamped));
    onChange(clamped === PLAYBACK_RATE_DEFAULT ? undefined : clamped);
  };

  /**
   * `<input type="number">` の `change` イベントハンドラ。
   *
   * ▲▼ スピナー操作 / 直接入力の両方でここに来る。従来は `rawInput` (ローカル state)
   * のみを更新し、確定は blur / Enter に任せていたが、それだと
   * 「▲▼で値を変えた直後に他の操作 (条件追加など) をすると値が反映されない／
   *   blur のタイミング次第で反映有無がブレる」という不安定な挙動になっていた。
   *
   * そこで **入力値が数値として妥当かつ許容範囲内なら即座に親へ通知** し、
   * プリセットボタンと同じ「クリック即確定」の体験に揃える。
   * 入力途中 (空文字 / `-` / `0.` など) や範囲外の値は commit せず `rawInput` の
   * 更新にとどめ、従来どおり blur / Enter でクランプ確定させる。
   */
  const handleRawInputChange = (raw: string): void => {
    setRawInput(raw);
    if (raw.trim() === '') return;
    const parsed = Number(raw);
    if (!isValidPlaybackRate(parsed)) return;
    onChange(parsed === PLAYBACK_RATE_DEFAULT ? undefined : parsed);
  };

  return (
    // 「見出し + プリセット群 + 自由入力 + ヒント」を 1 段の flex 行にまとめて
    // 縦方向の間延びを抑える。狭幅では flexWrap で自然に折り返す。
    <div
      className="playback-rate-field"
      data-testid={`content-playback-rate-field-${contentIndex}`}
      style={{
        margin: '6px 0',
        padding: '6px 8px',
        background: '#f8f9fa',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <strong>再生速度</strong>
      <div
        role="group"
        aria-label={`コンテンツ#${contentIndex + 1} 再生速度プリセット`}
        style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
      >
        {PLAYBACK_RATE_PRESETS.map((rate) => {
          const isActive = effective === rate;
          return (
            <button
              key={rate}
              type="button"
              className={`playback-rate-preset${isActive ? ' is-active' : ''}`}
              data-testid={`content-playback-rate-preset-${contentIndex}-${rate}`}
              aria-pressed={isActive}
              aria-label={`再生速度 ${rate} 倍`}
              onClick={() => handlePresetClick(rate)}
              style={{
                padding: '2px 8px',
                borderRadius: 4,
                border: '1px solid #ccc',
                background: isActive ? '#0969da' : '#fff',
                color: isActive ? '#fff' : 'inherit',
                cursor: 'pointer',
                fontWeight: isActive ? 'bold' : 'normal',
                // 数値幅がばらついても左右のブレを抑えるため最小幅を固定する。
                minWidth: 46,
                textAlign: 'center',
              }}
            >
              {/* 小数点1桁で統一 (0.5x / 0.75x / 1.0x / 1.5x など) */}
              {rate.toFixed(rate === Math.floor(rate) || (rate * 10) % 1 === 0 ? 1 : 2)}x
            </button>
          );
        })}
      </div>
      {/*
        自由入力欄は「[入力欄] 倍」の 1 行にまとめる。
        画面上の「自由入力」ラベルは冗長なため省略し、スクリーンリーダー向けの説明は
        aria-label に残す (a11y は維持したまま視覚的なノイズだけを削る)。

        注意: グローバル CSS (`styles.css` の `label { display: flex; flex-direction: column }`)
        が全 <label> を縦積みにするため、`flexDirection: 'row'` を明示的に指定して
        上書きする必要がある (指定しないと入力欄と「倍」が改行されてしまう)。
        併せて `whiteSpace: 'nowrap'` で折り返しも防ぐ。
      */}
      <label
        style={{
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          whiteSpace: 'nowrap',
        }}
      >
        <input
          type="number"
          min={PLAYBACK_RATE_MIN}
          max={PLAYBACK_RATE_MAX}
          step={0.05}
          aria-label={`コンテンツ#${contentIndex + 1} 再生速度 (自由入力)`}
          data-testid={`content-playback-rate-input-${contentIndex}`}
          value={rawInput}
          onChange={(e) => handleRawInputChange(e.target.value)}
          onBlur={(e) => commitRawInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitRawInput((e.target as HTMLInputElement).value);
            }
          }}
          style={{ width: 72 }}
        />
        <span>倍</span>
      </label>
      <span className="hint" style={{ fontSize: 12, color: '#666' }}>
        (0.5〜2.0, 1.0=等倍)
      </span>
    </div>
  );
}
